// Owned browser identity — a single, stable, first-party id minted synchronously
// so it is available before any deferred analytics init. It is the SAME id that:
//   • is stamped into every beacon event's params (`distinct_id`) → events_raw, and
//   • is recorded as the order-notes identity at checkout (audit/razorpay),
// so the dbt funnel mart reconciles browser sessions to server-canonical orders
// at ~100% (vs ~13% when events keyed on the GA client-id and orders on this id).
//
// Leaf module (imports nothing from analytics.ts / analytics-server.ts) so both
// can depend on it without a circular import.

const BOOTSTRAP_STORAGE_KEY = "ishvaram_analytics_distinct_id";
const LEGACY_BOOTSTRAP_STORAGE_KEY = "ishvaram_posthog_bootstrap_distinct_id";

function generateBootstrapId(): string {
  // Prefer crypto.randomUUID (all modern browsers + secure mobile contexts).
  // Fall back to a UUIDv4-shaped Math.random id for the rare insecure context so
  // we never return a collision-prone constant like "anon".
  try {
    const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // crypto unavailable — fall through.
  }
  const rand = () =>
    Math.floor(Math.random() * 0xffffffff)
      .toString(16)
      .padStart(8, "0");
  return `${rand()}-${rand().slice(0, 4)}-4${rand().slice(0, 3)}-${rand().slice(0, 4)}-${rand()}${rand().slice(0, 4)}`;
}

/**
 * Returns the stable browser-scoped client id, generating + persisting one on the
 * first call. Idempotent across reloads; safe to call repeatedly.
 */
export function getOrCreateBootstrapDistinctId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const existing =
      window.localStorage.getItem(BOOTSTRAP_STORAGE_KEY) ??
      window.localStorage.getItem(LEGACY_BOOTSTRAP_STORAGE_KEY);
    if (existing && !window.localStorage.getItem(BOOTSTRAP_STORAGE_KEY)) {
      window.localStorage.setItem(BOOTSTRAP_STORAGE_KEY, existing);
    }
    if (existing) return existing;
    const fresh = generateBootstrapId();
    window.localStorage.setItem(BOOTSTRAP_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage disabled (Safari private mode, etc.) — fall back to an
    // in-memory id. Loses cross-reload persistence but still bridges the
    // checkout request to the session's events within a single session.
    const w = window as Window & { __ishvaramBootstrapDistinctId?: string };
    if (!w.__ishvaramBootstrapDistinctId) {
      w.__ishvaramBootstrapDistinctId = generateBootstrapId();
    }
    return w.__ishvaramBootstrapDistinctId;
  }
}
